
import { defineComponent, ref, computed, watch, onMounted, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import InlineMessage from 'primevue/inlinemessage';
import ProgressSpinner from 'primevue/progressspinner';
import Tooltip from 'primevue/tooltip';

import { useGoogleFonts } from '@/composables/google-fonts/useGoogleFonts';
import { GoogleFont, EMAIL_SAFE_FONTS, FONT_CATEGORIES } from '@/composables/google-fonts/GoogleFontsService';

const FONTS_PER_PAGE = 30;

export default defineComponent({
  name: 'GoogleFontPickerModal',

  components: {
    Dialog,
    Button,
    InputText,
    Dropdown,
    InlineMessage,
    ProgressSpinner,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    currentFont: {
      type: String,
      default: '',
    },
    isEmailTemplate: {
      type: Boolean,
      default: false,
    },
    iframeDocument: {
      type: Object as PropType<Document | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'select', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Google Fonts composable
    const {
      fonts,
      isLoading,
      filteredFonts,
      favoriteFonts,
      recentFontsList,
      emailSafeFonts,
      categories,
      favorites,
      loadFonts,
      addToFavorites,
      removeFromFavorites,
      isFavorite,
      addToRecent,
      injectFontToIframe,
      generateFontValue,
      isEmailSafe,
    } = useGoogleFonts();

    // Local state
    const searchQuery = ref('');
    const selectedCategory = ref('all');
    // Default to emailSafe tab for email templates
    const activeTab = ref(props.isEmailTemplate ? 'emailSafe' : 'all');
    const selectedFont = ref('');
    const previewText = ref('');
    const displayLimit = ref(FONTS_PER_PAGE);

    // Visibility control
    const isVisible = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value),
    });

    // Category options for dropdown
    const categoryOptions = computed(() => FONT_CATEGORIES);

    // Get category icon
    const getCategoryIcon = (categoryId: string): string => {
      const category = FONT_CATEGORIES.find((c) => c.id === categoryId);
      return category?.icon || 'fas fa-font';
    };

    // Get category label
    const getCategoryLabel = (categoryId: string): string => {
      const category = FONT_CATEGORIES.find((c) => c.id === categoryId);
      return category ? t(category.label) : categoryId;
    };

    // Displayed fonts based on active tab and filters
    const displayedFonts = computed(() => {
      let result: GoogleFont[] = [];

      switch (activeTab.value) {
        case 'favorites':
          result = favoriteFonts.value;
          break;
        case 'recent':
          result = recentFontsList.value;
          break;
        case 'emailSafe':
          // Convert email safe fonts to GoogleFont format
          result = EMAIL_SAFE_FONTS.map((f) => ({
            family: f.family,
            category: f.category,
            variants: ['regular', 'bold'],
            subsets: ['latin'],
            version: '',
            lastModified: '',
            files: {},
          }));
          break;
        default:
          result = filteredFonts.value;
      }

      // Apply search filter
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((font) => font.family.toLowerCase().includes(query));
      }

      // Apply category filter (except for emailSafe tab)
      if (selectedCategory.value !== 'all' && activeTab.value !== 'emailSafe') {
        result = result.filter((font) => font.category === selectedCategory.value);
      }

      // Limit displayed fonts
      return result.slice(0, displayLimit.value);
    });

    // Check if there are more fonts to load
    const hasMoreFonts = computed(() => {
      if (activeTab.value === 'favorites') {
        return displayLimit.value < favoriteFonts.value.length;
      }
      if (activeTab.value === 'recent') {
        return displayLimit.value < recentFontsList.value.length;
      }
      if (activeTab.value === 'emailSafe') {
        return false;
      }
      return displayLimit.value < filteredFonts.value.length;
    });

    // Load more fonts
    const loadMoreFonts = (): void => {
      displayLimit.value += FONTS_PER_PAGE;
    };

    // Handle search input
    const handleSearch = (): void => {
      displayLimit.value = FONTS_PER_PAGE;
    };

    // Select a font
    const selectFont = (font: GoogleFont): void => {
      selectedFont.value = font.family;

      // Inject font for preview if iframe is provided
      if (props.iframeDocument) {
        injectFontToIframe(font.family, props.iframeDocument);
      }
    };

    // Toggle favorite
    const toggleFavorite = (fontFamily: string): void => {
      if (isFavorite(fontFamily)) {
        removeFromFavorites(fontFamily);
      } else {
        addToFavorites(fontFamily);
      }
    };

    // Check if email safe
    const isEmailSafeFont = (fontFamily: string): boolean => isEmailSafe(fontFamily);

    // Track loaded fonts (using object for reactivity)
    const loadedFontsMap = ref<Record<string, boolean>>({});

    // Load font for preview - inject link and let browser handle it
    const loadFontForPreview = (fontFamily: string): void => {
      if (loadedFontsMap.value[fontFamily] || isEmailSafe(fontFamily)) {
        return;
      }

      // Mark as loading
      loadedFontsMap.value[fontFamily] = true;

      // Check if already in DOM
      const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`);
      if (existingLink) {
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-font', fontFamily);
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@400;700&display=swap`;
      document.head.appendChild(link);
    };

    // Load font on hover for better UX
    const loadFontOnHover = (font: GoogleFont): void => {
      loadFontForPreview(font.family);
    };

    // Get font preview style - always use the font name, browser will use fallback until loaded
    const getFontPreviewStyle = (font: GoogleFont): Record<string, string> => {
      // Load font if not already loading
      if (!loadedFontsMap.value[font.family] && !isEmailSafe(font.family)) {
        loadFontForPreview(font.family);
      }

      // For email-safe fonts
      if (isEmailSafe(font.family)) {
        return {
          fontFamily: generateFontValue(font.family, true),
        };
      }

      // Always return the font name - browser will apply when loaded
      return {
        fontFamily: `"${font.family}", ${font.category}`,
      };
    };

    // Handle select
    const handleSelect = (): void => {
      if (selectedFont.value) {
        addToRecent(selectedFont.value);

        const fontValue = generateFontValue(selectedFont.value);
        const fontData = {
          family: selectedFont.value,
          value: fontValue,
          isEmailSafe: isEmailSafe(selectedFont.value),
        };

        emit('select', fontData);
        isVisible.value = false;
      }
    };

    // Handle cancel
    const handleCancel = (): void => {
      emit('cancel');
      isVisible.value = false;
    };

    // Handle close
    const handleClose = (): void => {
      emit('cancel');
    };

    // Watch visibility to load fonts and reset state
    watch(() => props.visible, async (newVisible) => {
      if (newVisible) {
        // Reset state
        displayLimit.value = FONTS_PER_PAGE;
        selectedFont.value = props.currentFont || '';
        activeTab.value = 'all';

        // Load fonts if not already loaded
        if (fonts.value.length === 0) {
          await loadFonts();
        }

        // Preload fonts for preview
        preloadFontsForPreview();
      }
    });

    // Preload first batch of fonts for preview
    const preloadFontsForPreview = (): void => {
      const fontsToPreload = filteredFonts.value.slice(0, 30);
      fontsToPreload.forEach((font) => {
        loadFontForPreview(font.family);
      });
    };

    // Load fonts on mount if visible
    onMounted(() => {
      if (props.visible && fonts.value.length === 0) {
        loadFonts();
      }
    });

    return {
      t,
      isVisible,
      isLoading,
      searchQuery,
      selectedCategory,
      activeTab,
      selectedFont,
      previewText,
      displayedFonts,
      hasMoreFonts,
      categoryOptions,
      favorites,
      getCategoryIcon,
      getCategoryLabel,
      loadMoreFonts,
      handleSearch,
      selectFont,
      toggleFavorite,
      isFavorite,
      isEmailSafeFont,
      getFontPreviewStyle,
      loadFontOnHover,
      handleSelect,
      handleCancel,
      handleClose,
    };
  },
});
